// src/components/sidebar/Sidebar.jsx
"use client";

import React, { useContext, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import GlassPanel from "../ui/GlassPanel";
import { User, Building2, Check, LogOut, PanelLeftClose, PanelLeftOpen, Settings as SettingsIcon } from "lucide-react";
import { RAIL_DESTINATIONS, RAIL_UTILITIES, SEPARATOR_AFTER } from "../../lib/navigation";
import { useCommandPalette, shortcutLabel } from "../CommandPalette/CommandPaletteContext";
import { Search } from "lucide-react";
import * as Tooltip from "@radix-ui/react-tooltip";
import { cn } from "../../lib/utils";
import logo from "../../assets/Logo.webp";
import defaultAvatar from "../../assets/avatar.webp";
import AuthContext from "../../AuthContext";
import { useOrganizations } from "../../hooks/useOrganizations";
import { useIsMobile } from "../../hooks/useMediaQuery";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "../ui/dropdown-menu";

const TIP_CLASS =
  "z-[90] select-none rounded-lg border border-white/10 bg-[#1C1431] px-2.5 py-1.5 text-xs font-medium text-white shadow-[0_8px_24px_rgba(0,0,0,0.55)] data-[state=delayed-open]:animate-in data-[state=delayed-open]:fade-in-0 data-[state=delayed-open]:zoom-in-95";

// El ancho del rail lo publica el propio Sidebar como --rail-w para que las
// vistas se desplacen solas. Ver `.rail-pad` / `.rail-mar` / `.rail-left` en App.css.
const RAIL_W = { collapsed: "5rem", expanded: "14rem" };
const STORAGE_KEY = "zenit.sidebar.collapsed";
// Por debajo de esto el rail arranca plegado, salvo que el usuario ya haya
// elegido: su decisión guardada siempre gana sobre el default por tamaño.
const NARROW_QUERY = "(max-width: 1024px)";

function hasStoredPreference() {
  try {
    return window.localStorage.getItem(STORAGE_KEY) !== null;
  } catch {
    return false;
  }
}

function readStoredCollapsed() {
  try {
    const stored = window.localStorage.getItem(STORAGE_KEY);
    if (stored !== null) return stored === "1";
    return window.matchMedia(NARROW_QUERY).matches;
  } catch {
    return false;
  }
}

// Una fila del rail. Plegado es un botón cuadrado con tooltip; desplegado es una
// fila con el nombre a la vista, que es el punto de todo esto.
function RailItem({ Icon, label, isActive, collapsed, onClick }) {
  const button = (
    <button
      onClick={onClick}
      aria-current={isActive ? "page" : undefined}
      aria-label={collapsed ? label : undefined}
      className={cn(
        "flex items-center rounded-2xl transition",
        "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-purple-400/70",
        collapsed ? "h-11 w-11 justify-center" : "h-11 w-full gap-3 px-3",
        isActive ? "bg-[#1C1431] shadow-sm" : "hover:bg-white/10"
      )}
    >
      <Icon
        className={cn("h-6 w-6 shrink-0", isActive ? "text-white" : "text-white/80")}
        strokeWidth={2}
      />
      {!collapsed && (
        <span
          className={cn(
            "truncate text-sm",
            isActive ? "font-semibold text-white" : "font-medium text-white/80"
          )}
        >
          {label}
        </span>
      )}
    </button>
  );

  // Desplegado el nombre ya se lee: un tooltip que repite la etiqueta es ruido.
  if (!collapsed) return button;

  return (
    <Tooltip.Root>
      <Tooltip.Trigger asChild>{button}</Tooltip.Trigger>
      <Tooltip.Portal>
        <Tooltip.Content side="right" sideOffset={12} className={TIP_CLASS}>
          {label}
          <Tooltip.Arrow className="fill-[#1C1431]" width={11} height={5} />
        </Tooltip.Content>
      </Tooltip.Portal>
    </Tooltip.Root>
  );
}

export default function Sidebar({ active = "Home", onSelect }) {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { openPalette } = useCommandPalette();
  const { user, logout: performLogout } = useContext(AuthContext);
  const { currentContext, personalAccount, organizations = [], changeContext } = useOrganizations();

  const userName = user?.username || user?.name || "My Account";
  const userEmail = user?.email || "";
  const userAvatar = user?.avatar_url || user?.avatarUrl || defaultAvatar;

  const isPersonalActive = currentContext?.type === "PERSONAL";

  const handleContextChange = async (nextContext) => {
    try {
      await changeContext(nextContext);
    } catch (error) {
      console.error("Failed to switch context", error);
    }
  };

  const handleLogout = () => {
    performLogout();
  };

  const [collapsed, setCollapsed] = useState(readStoredCollapsed);
  const [userChose, setUserChose] = useState(hasStoredPreference);
  const isMobile = useIsMobile();
  const toggleLabel = collapsed
    ? t("sidebar.expand", "Expand menu")
    : t("sidebar.collapse", "Collapse menu");

  // El ancho vive en una custom property para que las vistas de AppShell se
  // desplacen solas en vez de hardcodear el offset cada una por su lado.
  //
  // En móvil el rail está oculto y su ancho es cero. Tiene que decidirse acá y
  // no en un `@media` de App.css: esto escribe un estilo en línea sobre <html>,
  // y un estilo en línea le gana a cualquier regla de hoja de estilos.
  useEffect(() => {
    document.documentElement.style.setProperty(
      "--rail-w",
      isMobile ? "0px" : collapsed ? RAIL_W.collapsed : RAIL_W.expanded
    );
  }, [collapsed, isMobile]);

  // En pantallas bajas los 12 destinos no caben. Detectamos el desborde para
  // pintar un degradado abajo: sin esa pista el último destino queda invisible,
  // que es exactamente el problema que este rail viene a arreglar.
  const navListRef = useRef(null);
  const [overflowing, setOverflowing] = useState(false);

  useEffect(() => {
    const el = navListRef.current;
    if (!el || typeof ResizeObserver === "undefined") return undefined;
    const check = () => setOverflowing(el.scrollHeight > el.clientHeight + 1);
    check();
    const observer = new ResizeObserver(check);
    observer.observe(el);
    return () => observer.disconnect();
  }, [collapsed]);

  const toggleCollapsed = () =>
    setCollapsed((prev) => {
      const next = !prev;
      setUserChose(true);
      try {
        window.localStorage.setItem(STORAGE_KEY, next ? "1" : "0");
      } catch {
        /* modo privado: el rail sigue funcionando, solo no recuerda */
      }
      return next;
    });

  // Si la ventana se hace angosta, plegar; al ensanchar, volver a desplegar.
  // Solo mientras el usuario no haya tocado el toggle: en cuanto elige, su
  // decisión manda y el ancho de ventana deja de decidir por él.
  useEffect(() => {
    if (userChose || typeof window.matchMedia !== "function") return undefined;
    const mq = window.matchMedia(NARROW_QUERY);
    const apply = (e) => setCollapsed(e.matches);
    apply(mq);
    mq.addEventListener("change", apply);
    return () => mq.removeEventListener("change", apply);
  }, [userChose]);

  // Oculto por debajo de `md`: ahí navega MobileNav. Va con `hidden` y no
  // desmontado para que el rail no se vuelva a montar —ni recargue el avatar—
  // cada vez que se rota el teléfono o se redimensiona la ventana.
  return (
    <aside className="fixed top-0 left-0 bottom-0 z-[60] hidden md:block">
      <GlassPanel
        className={cn(
          "h-full p-2 !rounded-t-none !rounded-b-none transition-[width] duration-200",
          "[&>div:last-child]:h-full [&>div:last-child]:flex [&>div:last-child]:flex-col",
          collapsed ? "w-20 [&>div:last-child]:items-center" : "w-56 [&>div:last-child]:items-stretch"
        )}
      >
        <div
          className={cn(
            "flex w-full pt-2 pb-3",
            // Plegado no cabe logo + toggle en 80px: el toggle baja a su propia fila.
            collapsed ? "flex-col items-center gap-1" : "items-center gap-1 pl-1 pr-0.5"
          )}
        >
          <button
            onClick={() => onSelect("Zeno")}
            className="rounded-lg p-1 transition hover:bg-white/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-purple-400/70"
            aria-label="Go to Zeno"
          >
            <img src={logo} alt="Zenit" className="h-10 w-auto" />
          </button>
          {!collapsed && <span className="flex-1" />}
          <button
            onClick={toggleCollapsed}
            aria-expanded={!collapsed}
            aria-label={toggleLabel}
            title={toggleLabel}
            className={cn(
              "rounded-lg p-1.5 text-white/60 transition hover:bg-white/10 hover:text-white",
              "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-purple-400/70"
            )}
          >
            {collapsed ? <PanelLeftOpen className="h-4 w-4" /> : <PanelLeftClose className="h-4 w-4" />}
          </button>
        </div>

        <Tooltip.Provider delayDuration={120} skipDelayDuration={300}>
          <nav
            aria-label={t("sidebar.navLabel", "Main")}
            className="relative flex w-full flex-1 min-h-0 flex-col"
          >
            <button
              onClick={openPalette}
              aria-label={t("palette.open", "Search")}
              title={collapsed ? `${t("palette.open", "Search")} (${shortcutLabel()})` : undefined}
              className={cn(
                "mb-2 flex items-center rounded-2xl border border-white/10 bg-white/[0.04] text-white/50 transition",
                "hover:border-white/20 hover:bg-white/[0.08] hover:text-white/80",
                "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-purple-400/70",
                collapsed ? "h-11 w-11 shrink-0 justify-center self-center" : "h-10 w-full gap-2.5 px-3"
              )}
            >
              <Search className="h-4 w-4 shrink-0" />
              {!collapsed && (
                <>
                  <span className="flex-1 truncate text-left text-sm">{t("palette.open", "Search")}</span>
                  {/* El atajo se escribe dentro: si no se ve, no se descubre. */}
                  <kbd className="shrink-0 rounded border border-white/15 px-1.5 py-0.5 text-[10px] text-white/40">
                    {shortcutLabel()}
                  </kbd>
                </>
              )}
            </button>

            <ul
              ref={navListRef}
              className={cn(
                "flex w-full flex-1 min-h-0 flex-col overflow-y-auto",
                collapsed ? "items-center space-y-1" : "space-y-1"
              )}
            >
              {RAIL_DESTINATIONS.map((entry) => (
                <React.Fragment key={entry.id}>
                  <li className={cn("w-full", collapsed && "flex justify-center")}>
                    <RailItem
                      Icon={entry.Icon}
                      label={t(entry.i18nKey, entry.label)}
                      isActive={entry.id === active}
                      collapsed={collapsed}
                      onClick={() => onSelect(entry.id)}
                    />
                  </li>
                  {SEPARATOR_AFTER.has(entry.id) && (
                    <li role="presentation" aria-hidden="true" className="w-full py-1">
                      <hr className="border-0 border-t border-white/10" />
                    </li>
                  )}
                </React.Fragment>
              ))}
            </ul>
            {overflowing && (
              <div
                aria-hidden="true"
                className="pointer-events-none absolute inset-x-0 bottom-0 h-6 bg-gradient-to-t from-[#0a0118] to-transparent"
              />
            )}
          </nav>
        </Tooltip.Provider>

        <div className={cn("w-full mt-auto", collapsed ? "flex flex-col items-center" : "px-0.5")}>
          <hr className="mb-1 w-full border-0 border-t border-white/10" />
          <Tooltip.Provider delayDuration={120}>
            <ul className={cn("mb-1 w-full space-y-1", collapsed && "flex flex-col items-center")}>
              {RAIL_UTILITIES.map((entry) => (
                <li key={entry.id} className={cn("w-full", collapsed && "flex justify-center")}>
                  <RailItem
                    Icon={entry.Icon}
                    label={t(entry.i18nKey, entry.label)}
                    isActive={entry.id === active}
                    collapsed={collapsed}
                    onClick={() => onSelect(entry.id)}
                  />
                </li>
              ))}
            </ul>
          </Tooltip.Provider>
        </div>

        <div className={cn("w-full pb-2 flex", collapsed ? "justify-center" : "px-0.5")}>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                className={cn(
                  "flex items-center transition",
                  "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-purple-400/70",
                  collapsed
                    ? "h-11 w-11 justify-center rounded-full"
                    : "w-full gap-2.5 rounded-2xl p-1.5 hover:bg-white/10"
                )}
                aria-label="Open account menu"
              >
                <img
                  src={userAvatar}
                  alt="User avatar"
                  className="h-11 w-11 shrink-0 rounded-full border border-white/20 object-cover"
                  onError={(e) => {
                    e.target.src = defaultAvatar;
                  }}
                />
                {!collapsed && (
                  <span className="min-w-0 flex-1 truncate text-left text-sm font-medium text-white/80">
                    {userName}
                  </span>
                )}
              </button>
            </DropdownMenuTrigger>

            <DropdownMenuContent
              side="right"
              align="end"
              className="z-[80] w-64 bg-[#0a0a0a] border-white/20 text-white"
            >
              <DropdownMenuLabel className="space-y-1">
                <div className="font-medium">{userName}</div>
                {userEmail ? <div className="text-xs text-gray-400">{userEmail}</div> : null}
              </DropdownMenuLabel>

              <DropdownMenuSeparator className="bg-white/10" />

              <DropdownMenuLabel className="text-[11px] uppercase text-gray-400 tracking-wide">
                {t("sidebar.workspace", "Workspace")}
              </DropdownMenuLabel>

              <DropdownMenuItem
                onClick={() => handleContextChange({ type: "PERSONAL", id: personalAccount?.id })}
                className="cursor-pointer hover:bg-white/10 focus:bg-white/10"
              >
                <User className="w-4 h-4 mr-2" />
                <span className="flex-1">{t("sidebar.personal", "Personal")}</span>
                {isPersonalActive ? <Check className="w-4 h-4 text-purple-300" /> : null}
              </DropdownMenuItem>

              {organizations.map((org) => (
                <DropdownMenuItem
                  key={org.id}
                  onClick={() => handleContextChange({ type: "ORGANIZATION", id: org.id })}
                  className="cursor-pointer hover:bg-white/10 focus:bg-white/10"
                >
                  <Building2 className="w-4 h-4 mr-2" />
                  <span className="flex-1 truncate">{org.name}</span>
                  {currentContext?.type === "ORGANIZATION" && currentContext?.id === org.id ? (
                    <Check className="w-4 h-4 text-purple-300" />
                  ) : null}
                </DropdownMenuItem>
              ))}

              <DropdownMenuSeparator className="bg-white/10" />

              <DropdownMenuItem
                onClick={() => navigate("/organizations")}
                className="cursor-pointer hover:bg-white/10 focus:bg-white/10"
              >
                <Building2 className="w-4 h-4 mr-2" />
                {t("sidebar.organizations", "Organizations")}
              </DropdownMenuItem>

              <DropdownMenuItem
                onClick={() => navigate("/u/settings/profile")}
                className="cursor-pointer hover:bg-white/10 focus:bg-white/10"
              >
                <SettingsIcon className="w-4 h-4 mr-2" />
                {t("sidebar.settings", "Settings")}
              </DropdownMenuItem>

              <DropdownMenuSeparator className="bg-white/10" />

              <DropdownMenuItem
                onClick={handleLogout}
                className="cursor-pointer hover:bg-white/10 focus:bg-white/10"
              >
                <LogOut className="w-4 h-4 mr-2" />
                {t("sidebar.logout", "Logout")}
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </GlassPanel>
    </aside>
  );
}